import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { TILL_DOOR, navItemFor, visibleNav, type NavItem } from '@/lib/nav'
import { apiWrites, useLocations, useNotifications } from '@/lib/api'
import { signOut } from '@/lib/auth'
import { ALL, allowedLocations, canSwitch, currentUser, hasNoEstablishment, useScope } from '@/lib/scope'
import { useUnreadMessages } from '@/lib/messages'
import { factsFor, unreadActivite } from '@/lib/notifications'
import { Icon, Note, ScopeLabelCtx } from '@/ui'
import './shell.css'

/* The bell (wf00 F-8: it opens « Activité & alertes », Q14b) counts what the centre lists, nothing else:
   the unread facts of contract 11's feed (same query as the centre and Campagnes, so one cache) plus the
   retours privés still unread. The server keeps the read state; the centre marks what it has shown. */
function useBellCount() {
  const { data } = useNotifications()
  return unreadActivite(factsFor(data)) + useUnreadMessages()
}

/* ---------- D-31 · the establishment switcher — « Tous les établissements ▾ » or « Paris 9 ✕ » ----------
   One control answers « où suis-je ? »; every screen obeys it (lib/scope). A manager (D-46) sees
   her establishment as text, no ▾. Closes on Escape and outside click (M-10). */
function Switcher({ compact = false }: { compact?: boolean }) {
  const { data } = useLocations()
  const [scope, setScope] = useScope()
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const box = useRef<HTMLDivElement>(null)
  const all = (data ?? []).filter(l => l.status !== 'inactive')
  const mine = allowedLocations(all)
  const user = currentUser()
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false) }
    window.addEventListener('keydown', onKey); window.addEventListener('mousedown', onDown)
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('mousedown', onDown) }
  }, [open])
  const current = all.find(l => l.id === scope)
  /* one establishment, or a manager inside hers: a static name (ON-18 — no drop-down for one salon) */
  if (!canSwitch(all)) {
    /* a scoped user with nothing assigned: say so, never borrow the first establishment (0018 — an empty list is nothing) */
    if (data && hasNoEstablishment(all)) return <span className="sw-static"><Icon name="building" size={14} /> Aucun établissement attribué</span>
    const only = mine[0] ?? all[0]
    return <span className="sw-static"><Icon name="building" size={14} /> {only?.name ?? 'Votre établissement'}{user.role === 'manager' ? <span className="f-help"> · votre établissement</span> : ''}</span>
  }
  const shown = mine.filter(l => !q.trim() || l.name.toLowerCase().includes(q.trim().toLowerCase()))
  return (
    <div className={'sw' + (compact ? ' compact' : '')} ref={box}>
      {current ? (
        <span className="sw-chip">
          <button type="button" className="sw-chip-b" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(o => !o)}><Icon name="building" size={14} /> {current.name} <Icon name="chevron_down" size={12} /></button>
          <button type="button" className="sw-chip-x" aria-label="Revenir à tous les établissements" onClick={() => { setScope(ALL); setOpen(false) }}>×</button>
        </span>
      ) : (
        <button type="button" className="sw-b" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(o => !o)}><Icon name="building" size={14} /> Tous les établissements <Icon name="chevron_down" size={12} /></button>
      )}
      {open && (
        <div className="sw-pop" role="listbox" aria-label="Établissement">
          {mine.length >= 6 && <input className="in sw-q" placeholder="Rechercher un établissement…" value={q} onChange={e => setQ(e.target.value)} autoFocus />}
          <button type="button" role="option" aria-selected={scope === ALL} className={'sw-opt' + (scope === ALL ? ' on' : '')} onClick={() => { setScope(ALL); setOpen(false) }}>Tous les établissements <span className="f-help">· {all.length}</span></button>
          <div className="sw-list">
            {shown.map(l => (
              <button key={l.id} type="button" role="option" aria-selected={scope === l.id} className={'sw-opt' + (scope === l.id ? ' on' : '')} onClick={() => { setScope(l.id); setOpen(false) }}>
                {l.name} {l.status === 'pending' && <span className="f-help">· fiche à lier</span>}
              </button>
            ))}
            {!shown.length && <span className="f-help" style={{ padding: '6px 10px' }}>Aucun établissement ne correspond.</span>}
          </div>
          {user.role !== 'manager' && (
            <div className="sw-foot">
              <button type="button" className="tlink" onClick={() => { setOpen(false); nav('/etablissements?ajouter=1') }}>+ Ajouter un établissement</button>
              <button type="button" className="tlink" onClick={() => { setOpen(false); setScope(ALL); nav('/etablissements') }}>Gérer les établissements</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** The merchant shell — the handover menu in two groups (Version 21, lib/nav), the till door under the establishment,
 *  the establishment switcher in the topbar (D-31), a phone menu (M-03). Collapses to icons at the F-13 tablet breakpoint. */
export default function AppShell() {
  const { pathname, search } = useLocation()
  const nav = useNavigate()
  const unread = useBellCount()
  const { data: locations } = useLocations()
  const [scope] = useScope()
  const [menu, setMenu] = useState(false)
  /* A path belongs to its sidebar item: /cadeaux?onglet=roues is « Roues » (Cadeaux.tsx's tab; Demandes has its own page
     since lib-pack-17), « Cadeaux gagnés » is /clients?vue=cadeaux, /etablissement/:id (a network's context
     entry) belongs to Établissements; /etablissement alone is « Mon établissement ». */
  const ALIAS: Array<[string, string]> = [['/etablissement/', '/etablissements']]
  const q = new URLSearchParams(search)
  const navPath = pathname === '/clients' && q.get('vue') === 'integrations' ? '/integrations' /* the Intégrations door lives on Clients for now */
    : pathname === '/clients' && q.get('vue') === 'cadeaux' ? '/cadeaux/gagnes'
    : pathname === '/cadeaux' && q.get('onglet') === 'roues' ? '/roues'
    : ALIAS.find(([k]) => pathname === k || pathname.startsWith(k.endsWith('/') ? k : k + '/'))?.[1] ?? pathname
  const found = navItemFor(navPath, currentUser().role)
  const current = found?.item
  const scopeName = scope !== ALL ? (locations ?? []).find(l => l.id === scope)?.name ?? null : null
  const active = (locations ?? []).filter(l => l.status !== 'inactive')
  /* D-46 / 0018: a manager (or staff) with no establishment sees an empty espace that says so — never the network */
  const emptyEspace = locations !== undefined && hasNoEstablishment(active)
  const groups = visibleNav({ locations: active.length, role: currentUser().role }, navPath)
  /* a section (Bibliothèques, Clients, Paramètres) opens on its pages: open while one of them is current, or when the merchant opens it */
  const [opened, setOpened] = useState<Record<string, boolean>>({})
  const isOpen = (sec: NavItem) => opened[sec.path] ?? (found?.section?.path === sec.path)
  const toggle = (sec: NavItem) => setOpened(o => ({ ...o, [sec.path]: !isOpen(sec) }))
  useEffect(() => { setMenu(false) }, [pathname])
  useEffect(() => {
    if (!menu) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menu])

  /* the item lit is the one the path belongs to (navItemFor), never the router's prefix match alone: on « Roues »
     (/cadeaux?onglet=roues) the « Cadeaux » link is not lit */
  const link = (item: NavItem, sub = false) => (
    <NavLink key={item.path} to={item.path} end={item.path === '/'} title={item.label}
      className={({ isActive }) => 'side-item' + (sub ? ' sub' : '') + ((current ? current.path === item.path : isActive) ? ' on' : '')}>
      <Icon name={item.icon} size={18} /><span className="side-text">{item.label}</span>
    </NavLink>
  )
  const door = (
    <NavLink to={TILL_DOOR.path} title={TILL_DOOR.label} data-testid="side-door"
      className={({ isActive }) => 'side-item side-door' + ((current ? current.path === TILL_DOOR.path : isActive) ? ' on' : '')}>
      <Icon name={TILL_DOOR.icon} size={18} /><span className="side-text">{TILL_DOOR.label}</span>
    </NavLink>
  )
  /* lib-pack-18 (WF-047): the way out, under the menu — with an API only; the demo has no session to close */
  const out = apiWrites ? (
    <button type="button" className="side-item side-out" data-testid="side-out" title="Se déconnecter" onClick={() => { void signOut() }}>
      <Icon name="lock" size={18} /><span className="side-text">Se déconnecter</span>
    </button>
  ) : null
  const navBlock = (
    <nav aria-label="Navigation">
      {groups.map((group, gi) => (
        /* the second group is the establishment's utilities (Version 21: nav « Votre établissement ») */
        <div key={gi} className={'side-group' + (group.label ? ' util' : '')} aria-label={group.label ?? undefined}>
          {group.label && <div className="side-label">{group.label}</div>}
          {group.items.map(item => item.children ? (
            <div key={item.path} className={'side-sec' + (isOpen(item) ? ' open' : '')} data-testid={`nav-${item.label.toLowerCase()}`}>
              <div className={'side-item side-sec-h' + (found?.section?.path === item.path || current?.path === item.path ? ' on' : '')}>
                <NavLink to={item.path} title={item.label} className="side-sec-link" onClick={() => setOpened(o => ({ ...o, [item.path]: true }))}>
                  <Icon name={item.icon} size={18} /><span className="side-text">{item.label}</span>
                </NavLink>
                <button type="button" className="side-sec-t" aria-label={isOpen(item) ? `Replier ${item.label}` : `Déplier ${item.label}`} aria-expanded={isOpen(item)} onClick={() => toggle(item)}>
                  <Icon name={isOpen(item) ? 'chevron_up' : 'chevron_down'} size={14} />
                </button>
              </div>
              {isOpen(item) && <div className="side-sub">{item.children.map(c => link(c, true))}</div>}
            </div>
          ) : link(item))}
        </div>
      ))}
    </nav>
  )

  return (
    <ScopeLabelCtx.Provider value={scopeName}>
      <div className={'shell' + (scopeName ? ' in-scope' : '')}>
        <aside className="side" aria-label="Navigation principale">
          <div className="side-brand"><span className="side-mark" aria-hidden>b</span><span className="side-name">buildmyreviews</span></div>
          <div className="side-search" role="search"><Icon name="search" size={14} /><span>Rechercher…</span><kbd>/</kbd></div>
          {door}
          {navBlock}
          {out}
        </aside>
        <div className="main">
          <header className="topbar">
            <button type="button" className="topbar-menu" aria-label="Menu" aria-expanded={menu} onClick={() => setMenu(m => !m)}><Icon name="menu" size={18} /></button>
            <div className="topbar-where"><span className="topbar-ws">Valessio</span><span className="topbar-sep" aria-hidden>›</span><Switcher /></div>
            <h1 className="topbar-title">{current?.label ?? (pathname.startsWith('/guide') ? 'Démarrer' : 'Build my reviews')}</h1>
            <button type="button" className="topbar-bell" onClick={() => nav('/messages')}
              aria-label={unread > 0 ? `Activité & alertes — ${unread} non lues` : 'Activité & alertes'}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M18 15V10a6 6 0 1 0-12 0v5l-1.7 2.6a.6.6 0 0 0 .5.9h14.4a.6.6 0 0 0 .5-.9L18 15Z"
                  stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M10 20a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              {unread > 0 && <span className="bell-badge">{unread}</span>}
            </button>
          </header>
          {/* M-03 · the phone menu: the canonical nav as a sheet — nothing removed, one tap away (F-13) */}
          {menu && (
            <div className="nav-veil" onClick={e => { if (e.target === e.currentTarget) setMenu(false) }}>
              <div className="nav-sheet" role="dialog" aria-label="Navigation">
                <div className="nav-sheet-h"><span className="side-mark" aria-hidden>b</span><b>buildmyreviews</b><button type="button" className="toast-x" style={{ color: 'inherit', marginLeft: 'auto' }} aria-label="Fermer le menu" onClick={() => setMenu(false)}>×</button></div>
                <div className="nav-sheet-sw"><Switcher compact /></div>
                {door}
                {navBlock}
                {out}
              </div>
            </div>
          )}
          <main className="content">{emptyEspace ? <EmptyEspace /> : <Outlet />}</main>
        </div>
      </div>
    </ScopeLabelCtx.Provider>
  )
}

/** The empty espace of a scoped user with nothing assigned (D-46 · 0018: an empty list is nothing, not everything). */
function EmptyEspace() {
  return (
    <div className="page" data-testid="espace-vide">
      <h2 className="t-page" style={{ margin: 0 }}>Aucun établissement ne vous a été attribué.</h2>
      <Note tone="candour">Votre accès est prêt, mais il ne couvre encore aucun établissement. Un administrateur peut vous en confier un depuis <b>Équipe</b> — vous verrez alors ses campagnes, ses cadeaux et ses résultats, et rien d’autre.</Note>
    </div>
  )
}
